import YieldAnalysisAxiosInstance, {
  YIELD_ANALYSIS_API_URL,
} from "./yieldAnalysisAxios";

/**
 * Yield Analysis readiness, per the EXP008 contract.
 *
 * The endpoint is keyed on the *field*, not the trial: the harvest pipeline is
 * field-centric, so trial ids come back as output data. A field missing from
 * `fields[]` has no harvest ingestion at all, which the contract calls NOT
 * FOUND — readiness depends on the grower uploading a yield-monitor file, so a
 * perfectly configured trial can legitimately sit there for a whole season.
 */
export enum YieldAnalysisStatus {
  /** Plots with yield exist: link to Explorer. */
  Available = "AVAILABLE",
  /** Harvest file arrived less than 15 minutes ago; re-checking is worthwhile. */
  Pending = "PENDING",
  /** Harvest file is older than 15 minutes with no rows; re-checking will not help. */
  Failed = "FAILED",
  /** No harvest ingestion for the field, or nothing to report. */
  NotFound = "NOT_FOUND",
}

/** One field's readiness entry in the response. */
export interface YieldAnalysisField {
  field_id?: string | null;
  /**
   * Left as a plain string on purpose: this mirrors the wire, and EXP008 §10
   * reserves the right to ship additive changes without notice. `readStatus`
   * narrows it, treating anything unknown as NOT FOUND — typing it as the enum
   * here would claim a guarantee the response cannot give.
   */
  status?: string | null;
  trial_ids?: string[] | null;
  plots_with_yield?: number | null;
  notification_id?: string | null;
  harvest_received_at?: string | null;
}

export interface YieldAnalysisResponse {
  property_id?: string | null;
  organization_id?: string | null;
  year?: number | null;
  explorer_url?: string | null;
  fields?: YieldAnalysisField[] | null;
}

/** What the preview needs to render its tag and its Explorer link. */
export interface YieldAnalysisReadiness {
  status: YieldAnalysisStatus;
  /** Pre-built by the API; empty when there are no results to link to. */
  explorerUrl: string;
}

export interface YieldAnalysisParams {
  /** The trial's fields. Readiness is keyed on the field (EXP008 §3.1). */
  fieldIds?: string[] | null;
  /** The trial, matched against a field's `trial_ids` when it carries several. */
  trialId?: string | null;
  year?: number | string | null;
}

export const NOT_READY: YieldAnalysisReadiness = {
  status: YieldAnalysisStatus.NotFound,
  explorerUrl: "",
};

/**
 * Derives the year the endpoint should be queried with. Yield only exists after
 * harvest, so the trial's end date drives the season.
 * @param endDate The trial's end date.
 * @returns The four-digit year, or null when the date is missing/unparseable.
 */
export const getYieldAnalysisYear = (
  endDate?: string | null
): number | null => {
  if (!endDate) return null;
  const year = new Date(endDate).getFullYear();

  return Number.isNaN(year) ? null : year;
};

/** Reads a field entry's status, defaulting anything unrecognised to NOT FOUND. */
const readStatus = (field: YieldAnalysisField): YieldAnalysisStatus => {
  switch (field?.status) {
    case YieldAnalysisStatus.Available:
      return YieldAnalysisStatus.Available;
    case YieldAnalysisStatus.Pending:
      return YieldAnalysisStatus.Pending;
    case YieldAnalysisStatus.Failed:
      return YieldAnalysisStatus.Failed;
    default:
      return YieldAnalysisStatus.NotFound;
  }
};

/**
 * Whether a field's entry speaks for the given trial. A field can carry several
 * trials, and only those listed in `trial_ids` actually have results.
 * @param field The field entry.
 * @param trialId The trial being previewed.
 */
const coversTrial = (
  field: YieldAnalysisField,
  trialId?: string | null
): boolean => {
  const trialIds = Array.isArray(field?.trial_ids) ? field.trial_ids : [];

  // Only AVAILABLE entries carry trial ids, so an empty list cannot exclude
  // anything: fall back to the field itself being the match.
  if (trialIds.length === 0 || !trialId) return true;

  return trialIds.includes(trialId);
};

/**
 * Resolves a trial's readiness from a response, keeping the most advanced state
 * across the trial's fields: one ready field is enough to show results.
 * @param data The response body.
 * @param fieldIds The trial's fields.
 * @param trialId The trial being previewed.
 */
export const resolveTrialReadiness = (
  data: YieldAnalysisResponse | null | undefined,
  fieldIds?: string[] | null,
  trialId?: string | null
): YieldAnalysisReadiness => {
  const explorerUrl = data?.explorer_url ?? "";
  // Read into a local first: `Array.isArray` narrows the local, so there is no
  // second nullish fallback to write for a value already proven to be an array.
  const rawFields = data?.fields;
  const entries = Array.isArray(rawFields) ? rawFields : [];
  const wanted = Array.isArray(fieldIds) ? fieldIds : [];

  const relevant = entries.filter(
    (field) =>
      // A field-scoped response is already narrowed to one field.
      (wanted.length === 0 || wanted.includes(field?.field_id ?? "")) &&
      coversTrial(field, trialId)
  );

  const statuses = new Set(relevant.map(readStatus));

  /*
   * AVAILABLE is reported even without an `explorer_url`: the plots carry yield
   * either way, and saying "not ready" would deny results that exist. The link
   * is what gets disabled, not the state. Per EXP008 §5 the two cannot really
   * co-occur — the url is only null when `fields[]` is empty, and an empty list
   * has nothing to report AVAILABLE.
   */
  if (statuses.has(YieldAnalysisStatus.Available)) {
    return { status: YieldAnalysisStatus.Available, explorerUrl };
  }
  if (statuses.has(YieldAnalysisStatus.Pending)) {
    return { status: YieldAnalysisStatus.Pending, explorerUrl: "" };
  }
  if (statuses.has(YieldAnalysisStatus.Failed)) {
    return { status: YieldAnalysisStatus.Failed, explorerUrl: "" };
  }

  return NOT_READY;
};

/**
 * Combines per-field readiness into one answer for the trial, keeping the most
 * advanced state: one ready field is enough to show results.
 * @param results The readiness resolved for each of the trial's fields.
 */
const combineReadiness = (
  results: YieldAnalysisReadiness[]
): YieldAnalysisReadiness =>
  results.find((result) => result.status === YieldAnalysisStatus.Available) ??
  results.find((result) => result.status === YieldAnalysisStatus.Pending) ??
  results.find((result) => result.status === YieldAnalysisStatus.Failed) ??
  NOT_READY;

/**
 * Asks the Yield Analysis service whether a trial's results are ready.
 *
 * Queried at field scope (EXP008 §7.2), which returns at most one entry per
 * call — so a trial spanning several fields is asked about each of them and the
 * answers are combined. Network or server failures resolve to "not found" so a
 * failing call degrades the tag rather than breaking the preview around it.
 * @param params The trial's fields, its id and the season.
 */
export const fetchYieldAnalysisReadiness = async ({
  fieldIds,
  trialId,
  year,
}: YieldAnalysisParams): Promise<YieldAnalysisReadiness> => {
  // Without a configured host the call would resolve against the app's own
  // origin, so treat an unset URL as "nothing to report" instead.
  if (!YIELD_ANALYSIS_API_URL) {
    console.warn(
      "REACT_APP_YIELD_ANALYSIS_API_URL is not set; yield analysis is unavailable."
    );
    return NOT_READY;
  }

  const fields = (Array.isArray(fieldIds) ? fieldIds : []).filter(Boolean);
  if (fields.length === 0) return NOT_READY;

  const results = await Promise.all(
    fields.map(async (fieldId) => {
      try {
        const response = await YieldAnalysisAxiosInstance.get(
          "/trials/yield-status",
          // `year` is recommended, not required: without it the service reports
          // the latest ingestion regardless of season.
          { params: year ? { field_id: fieldId, year } : { field_id: fieldId } }
        );

        return resolveTrialReadiness(response?.data, [fieldId], trialId);
      } catch (error) {
        console.error("Error fetching yield analysis readiness:", error);
        return NOT_READY;
      }
    })
  );

  return combineReadiness(results);
};
